import type {
  StorefrontBuyerOrderStatusPayloadV1,
  StorefrontBuyerOrderDecisionPayloadV1,
  StorefrontOrderPayloadV1,
  StorefrontOrderRecord,
  StorefrontOrderStatus,
} from '@stokmerter/contracts';
import { useMemo, useState } from 'react';

import { checkoutLinePieces } from './checkout';

export type BuyerOrder = StorefrontOrderRecord & {
  orderDetail: StorefrontOrderPayloadV1 | null;
  statusDetail: StorefrontBuyerOrderStatusPayloadV1 | null;
  decisionDetail: StorefrontBuyerOrderDecisionPayloadV1 | null;
};

export type BuyerOrderFilter = 'all' | 'pending' | 'active' | 'completed' | 'rejected';
export type PageFeedback = { tone: 'error' | 'info' | 'success'; text: string };

const statusLabel: Record<StorefrontOrderStatus, string> = {
  pending_review: 'Firma incelemesinde',
  approved: 'Onaylandı',
  preparing: 'Hazırlanıyor',
  shipped: 'Gönderildi',
  completed: 'Tamamlandı',
  rejected: 'Reddedildi',
};

export function buyerOrderStatusLabel(status: StorefrontOrderStatus): string {
  return statusLabel[status];
}

const filters: ReadonlyArray<{ key: BuyerOrderFilter; label: string }> = [
  { key: 'all', label: 'Tümü' },
  { key: 'pending', label: 'İncelemede' },
  { key: 'active', label: 'Hazırlanan' },
  { key: 'completed', label: 'Tamamlanan' },
  { key: 'rejected', label: 'Reddedilen' },
];

function statusFilter(status: StorefrontOrderStatus): Exclude<BuyerOrderFilter, 'all'> {
  if (status === 'pending_review') return 'pending';
  if (status === 'completed') return 'completed';
  if (status === 'rejected') return 'rejected';
  return 'active';
}

export function filterBuyerOrders(orders: readonly BuyerOrder[], filter: BuyerOrderFilter): BuyerOrder[] {
  return filter === 'all' ? [...orders] : orders.filter((order) => statusFilter(order.status) === filter);
}

export function summarizeBuyerOrder(detail: StorefrontOrderPayloadV1 | null) {
  if (!detail) return null;
  return detail.items.reduce((summary, line) => ({
    lines: summary.lines + 1,
    series: summary.series + line.seriesQuantity,
    extras: summary.extras + line.extraQuantity,
    pieces: summary.pieces + checkoutLinePieces({
      seriesCount: line.seriesCount,
      seriesQuantity: line.seriesQuantity,
      extraQuantity: line.extraQuantity,
    }),
  }), { lines: 0, series: 0, extras: 0, pieces: 0 });
}

export function buyerOrderApprovedDetail(order: BuyerOrder): StorefrontOrderPayloadV1 | null {
  const detail = order.orderDetail;
  const decision = order.decisionDetail;
  if (!detail || decision?.outcome !== 'approved') return detail;
  const approved = new Map(decision.lines.map((line) => [
    `${line.productId}:${line.variantId}`,
    line,
  ]));
  return {
    ...detail,
    items: detail.items.map((line) => {
      const decided = approved.get(`${line.productId}:${line.variantId}`);
      return decided ? {
        ...line,
        seriesQuantity: decided.approvedSeriesQuantity,
        extraQuantity: decided.approvedExtraQuantity,
      } : line;
    }).filter((line) => line.seriesQuantity + line.extraQuantity > 0),
  };
}

function orderNumber(orderId: string): string {
  return orderId.slice(-10).toLocaleUpperCase('tr-TR');
}

export function BuyerOrdersPage(props: {
  feedback: PageFeedback | null;
  loading: boolean;
  onBack: () => void;
  onOpenOrder: (orderId: string) => void;
  onRefresh: () => void;
  orders: BuyerOrder[];
}) {
  const [filter, setFilter] = useState<BuyerOrderFilter>('all');
  const visibleOrders = useMemo(() => filterBuyerOrders(props.orders, filter), [filter, props.orders]);
  const counts = useMemo(() => Object.fromEntries(filters.map(({ key }) => [key,
    filterBuyerOrders(props.orders, key).length])) as Record<BuyerOrderFilter, number>, [props.orders]);

  return <section className="buyer-orders-page">
    <div className="buyer-orders-heading">
      <div>
        <button className="orders-back-button" onClick={props.onBack} type="button">← Ürünlere dön</button>
        <h1>Siparişlerim</h1>
        <p>Siparişlerinizi, ürün satırlarını ve firma tarafından güncellenen durumları burada izleyin.</p>
      </div>
      <button className="orders-refresh-button" disabled={props.loading} onClick={props.onRefresh} type="button">
        {props.loading ? 'Yenileniyor…' : 'Yenile'}
      </button>
    </div>

    {props.feedback ? <div aria-live="polite" className={`orders-page-feedback ${props.feedback.tone}`}
      role={props.feedback.tone === 'error' ? 'alert' : 'status'}>{props.feedback.text}</div> : null}

    <div aria-label="Sipariş durumuna göre filtrele" className="buyer-order-filters" role="group">
      {filters.map((item) => <button aria-pressed={filter === item.key}
        className={filter === item.key ? 'active' : ''} key={item.key}
        onClick={() => setFilter(item.key)} type="button">
        <span>{item.label}</span><b>{counts[item.key]}</b>
      </button>)}
    </div>

    {props.loading && props.orders.length === 0 ? <div className="orders-page-state">Siparişleriniz yükleniyor…</div>
      : props.orders.length === 0 ? <div className="orders-page-state"><b>Henüz siparişiniz yok.</b>
        <span>Ürünleri inceleyip ilk siparişinizi oluşturabilirsiniz.</span></div>
      : visibleOrders.length === 0 ? <div className="orders-page-state">Bu durumda bir siparişiniz yok.</div>
      : <div className="buyer-orders-list">{visibleOrders.map((order) => {
        const summary = summarizeBuyerOrder(buyerOrderApprovedDetail(order));
        return <article className={`buyer-order-card status-${order.status}`}
          key={order.orderId}>
          <div className="buyer-order-summary">
            <div className="buyer-order-identity"><span>Sipariş No</span><strong>{orderNumber(order.orderId)}</strong>
              <small>{new Date(order.createdAt).toLocaleString('tr-TR')}</small></div>
            <div className={`buyer-order-status ${order.status}`}>{statusLabel[order.status]}</div>
            {summary ? <div className="buyer-order-metrics">
              <div><span>Ürün</span><b>{summary.lines}</b></div>
              <div><span>Seri</span><b>{summary.series}</b></div>
              <div><span>Ek adet</span><b>{summary.extras}</b></div>
              <div><span>Toplam parça</span><b>{summary.pieces}</b></div>
            </div> : <div className="buyer-order-metrics unavailable">Ayrıntı özeti bulunmuyor</div>}
            <button className="buyer-order-detail-button"
              onClick={() => props.onOpenOrder(order.orderId)} type="button">
              Sipariş detayı<span aria-hidden="true">›</span>
            </button>
          </div>

          {order.decisionDetail?.message || order.statusDetail?.message
            ? <p className="buyer-order-message">
              {order.decisionDetail?.message ?? order.statusDetail?.message}</p>
            : null}
        </article>;
      })}</div>}
  </section>;
}
